import { useState } from "react";
import { motion } from "framer-motion";
import {
  CalendarDays,
  CreditCard,
  CalendarX,
  RotateCcw,
  UserRound,
  CarFront,
  Mail,
  Send,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { LPlateMark } from "@/components/illustrations/parts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "bookings", icon: CalendarDays, title: "Bookings", desc: "Help with booking lessons, rescheduling and availability." },
  { id: "payments", icon: CreditCard, title: "Payments", desc: "Questions about paying online with Stripe or paying cash." },
  { id: "cancellations", icon: CalendarX, title: "Cancellations", desc: "Cancelling a lesson and understanding cancellation options." },
  { id: "refunds", icon: RotateCcw, title: "Refunds", desc: "Refund requests and when to expect your money back." },
  { id: "accounts", icon: UserRound, title: "Accounts", desc: "Profile details, sign-in issues and account settings." },
  { id: "instructor-subscriptions", icon: CarFront, title: "Instructor Subscriptions", desc: "Support for instructors about joining and subscription plans." },
];

const Support = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }
      toast.success("Your message has been sent. The DELSHA team will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(
        err.message === "Failed to fetch"
          ? "Could not send your message. Please email us directly instead."
          : err.message,
      );
    } finally {
      setSending(false);
    }
  };

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div data-testid="support-page" className="pt-28 md:pt-36">
      <Seo
        title="Contact & Support — DELSHA"
        description="Contact DELSHA support — help with bookings, payments, cancellations, refunds, accounts and instructor subscriptions, plus our contact form."
      />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-blue-600">
            Contact &amp; Support
          </p>
          <div className="relative mt-4 max-w-3xl">
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              We're here to help.
            </h1>
            <motion.svg
              viewBox="0 0 66 66"
              className="absolute -right-4 top-0 hidden h-16 w-16 rotate-6 sm:block"
              data-testid="support-lplate-illustration"
              animate={{ y: [0, -8, 0], rotate: [6, 10, 6] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
              aria-hidden="true"
            >
              <LPlateMark />
            </motion.svg>
          </div>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Find help with the most common DELSHA topics below, or send us a
            message using the contact form.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((cat, i) => (
            <Reveal key={cat.id} delay={i * 0.08}>
              <div
                data-testid={`help-category-${cat.id}`}
                className="group h-full rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-transform duration-300 group-hover:scale-110">
                  <cat.icon className="h-6 w-6" />
                </span>
                <h2 className="mt-6 font-display text-lg font-semibold text-slate-900">
                  {cat.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {cat.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-16 border-t border-slate-200/80 bg-slate-50 py-16 md:mt-24 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr] lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Send us a message
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Fill in the form and your message will be emailed straight to the
              DELSHA support team.
            </p>

            <div
              data-testid="support-email-card"
              className="mt-8 flex items-start gap-4 rounded-2xl border border-slate-200/80 bg-white p-6"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-base font-semibold text-slate-900">
                  Support email
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  Prefer email? Contact us directly at:
                </p>
                <a
                  href="mailto:delsha.find.book.learn@gmail.com"
                  data-testid="support-email-link"
                  className="mt-2 inline-block break-all text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700"
                >
                  delsha.find.book.learn@gmail.com
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm sm:p-9"
            >
              <div className="grid gap-5">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="mb-2 block text-sm font-medium text-slate-900"
                  >
                    Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    required
                    maxLength={100}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="mb-2 block text-sm font-medium text-slate-900"
                  >
                    Email
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-message"
                    className="mb-2 block text-sm font-medium text-slate-900"
                  >
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    maxLength={3000}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="How can we help?"
                    className="w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send message
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Support;
